import React, { forwardRef, ChangeEventHandler, FocusEventHandler } from "react";
// External dependencies
import classNames from "classnames";
import { InputFieldCss } from "./inputFieldContainer";

interface InputFieldProps {
  name: string;
  id?: string;
  value?: string;
  placeholder: string;
  className?: string;
  labelName?: string | false;
  required?: string | boolean;
  disabled?: string | boolean;
  showIcon?:  boolean;
  disableFutureDate?:  boolean;
  inputIcon?: React.ReactNode;
  register: any;
  errorMessage?: React.ReactNode;
  inputType?: "text" | "date" | "number" | "password";
  defaultValue?: string | number;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onIconClick?: () => void;
  hiddenLabel?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>; // Parent-provided onChange
}

const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      name,
      id = "",
      value,
      placeholder,
      className,
      labelName = false,
      required,
      register,
      errorMessage = "",
      inputType = "text",
      defaultValue = "",
      hiddenLabel = false,
      onBlur,
      onKeyDown,
      onIconClick,
      onChange, // Parent onChange
      showIcon = false,
      inputIcon,
      disableFutureDate = true,
      ...props
    },
    ref
  ) => {
    const inputClasses = classNames(
      "form-control",
      "form-control-sm",
      "m-input",
      "search-icons",
      className
    );

    const today = (new Date().toISOString().split("T")[0]) as string;

    const { onBlur : registerOnBlur, onChange: registerOnChange, ...registeredProps } = register(name, {
      validate: (value: any) => {
        if (inputType === "date" && new Date(value) > new Date(today)) {
          return "Date cannot be in the future";
        }
        return true;
      },
    });

    return (
      <InputFieldCss>
        <div className="form-group">
          {labelName && (
            <label className="input__textfield" style={{ visibility : hiddenLabel ? "hidden" : "visible" }} htmlFor={id}>
              {labelName}
              {required && <span className="common__required">*</span>}
            </label>
          )}
            <div className={showIcon ? "input-container" : ""}>
                {showIcon && <div className="currency-symbol" onClick={onIconClick}>{inputIcon}</div>}
                <input
                    type={inputType}
                    className={inputClasses}
                    name={name}
                    placeholder={placeholder}
                    id={id}
                    value={value} // Controlled input
                    defaultValue={defaultValue} // Uncontrolled input
                    onKeyDown={onKeyDown}
                    onBlur={(e) => {
                        if (onBlur) onBlur(e); // Call parent-provided onBlur
                        registerOnBlur(e); // Call react-hook-form's onBlur if it exists
                    }}
                    ref={ref}
                    onChange={(e) => {
                        registerOnChange(e); // Call react-hook-form's onChange
                        if (onChange) onChange(e); // Call parent-provided onChange
                    }}
                    {...registeredProps} // Spread remaining register properties
                    {...(inputType === "date" && { max: disableFutureDate ? today : "2100-12-31" })}
                    {...props}
                />
            </div>
            {errorMessage && <div className="error-message">{errorMessage}</div>}
        </div>
      </InputFieldCss>
    );
  }
);

export default InputField;
